import React, { useId, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import s from './DateField.module.css';

/**
 * O3 (SO-20) — a date input that always SHOWS the UAE convention (DD/MM/YYYY, or the
 * Arabic equivalent) instead of the browser locale's format (`mm/dd/yyyy` on an
 * en-US browser). The value stays ISO `yyyy-MM-dd`, exactly like a native
 * `<input type="date">`, and picking still uses the native date picker: the visible
 * text is a formatted label over a transparent native input.
 */
export interface DateFieldProps {
    /** ISO `yyyy-MM-dd`, or '' when empty. */
    value: string;
    onChange: (iso: string) => void;
    /** Accessible name (also used as the placeholder when empty). */
    label: string;
    min?: string;
    max?: string;
    disabled?: boolean;
    id?: string;
    className?: string;
    /** Text shown when empty (defaults to the label). */
    placeholder?: string;
}

/** `yyyy-MM-dd` → `DD/MM/YYYY` (en) or Arabic day/month/year, without timezone shifts. */
export function formatIsoDateForDisplay(iso: string, language: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return '';
    const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return date.toLocaleDateString(language === 'ar' ? 'ar' : 'en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
    });
}

const DateField: React.FC<DateFieldProps> = ({
    value, onChange, label, min, max, disabled, id, className, placeholder,
}) => {
    const { language } = useLanguage();
    const autoId = useId();
    const inputId = id ?? autoId;
    const inputRef = useRef<HTMLInputElement>(null);
    const shown = formatIsoDateForDisplay(value, language);

    return (
        <div className={`${s.field} ${disabled ? s.disabled : ''} ${className ?? ''}`} data-testid="date-field">
            <span className={`${s.text} ${shown ? '' : s.placeholder}`} aria-hidden="true">
                {shown || placeholder || label}
            </span>
            <span className={s.icon} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
                    <path d="M8 3v4M16 3v4M3.5 10h17" />
                </svg>
            </span>
            <input
                ref={inputRef}
                id={inputId}
                className={s.native}
                type="date"
                value={value}
                min={min}
                max={max}
                disabled={disabled}
                aria-label={label}
                onChange={e => onChange(e.target.value)}
                onKeyDown={e => {
                    // Typing would follow the browser locale's segment order (mm/dd on
                    // en-US) while the field shows DD/MM — open the picker instead.
                    if (/^[0-9]$/.test(e.key) || e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        try { inputRef.current?.showPicker?.(); } catch { /* not supported */ }
                    }
                }}
                onClick={() => {
                    try { inputRef.current?.showPicker?.(); } catch { /* not supported */ }
                }}
            />
        </div>
    );
};

export default DateField;
